import { useQuery } from '@apollo/client';
import {
  formatGroupLabel,
  IndicatorSeparator
} from '@nile/banyan/src/Pebbles/select';
import { DropdownArrow } from '@nile/mangifera/src/Pebbles/select';
import {
  getTranslatedText,
  Loader,
  useProduct,
  COMMON_CONSTANTS
} from '@nile/components';
import {
  AccountClient,
  GET_RULEBUILDER_OPERATORS,
  GET_TICKET_LISTVIEW_META,
  useCachedQuery,
  RMMClient
} from '@nile/graphql';
import { MAX_METRIC_COUNT } from '@nile/reports/src/modules/charts/constants';
import ChartContext from '@nile/reports/src/modules/charts/contexts/Chart';
import { GET_REPORT_MODULES } from '@nile/reports/src/modules/reports/graphql';
import { GET_ALERT_META_INFO_V4 } from '@nile/rmm/src/modules/alert-management/Graphql/alert';
import { GET_ASSET_VIEW_META_INFO } from '@nile/rmm/src/modules/asset-management/Graphql/asset';
import { getGlobalEntityAttributes } from '@nile/reports/src/modules/component/helper';
import {
  GET_PATCH_DETAILS_LISTVIEW_META,
  GET_CONDITIONS_META_FOR_RMM_REPORTS,
  GET_SOFTWARELIST_META,
  GET_NMAsset_META,
  GET_ALL_ASSETS_META
} from '@nile/rmm/src/modules/patch-management/Graphql/patch';
import { GET_CONDITIONS_META_FOR_INVOICE } from '@nile/graphql/src/Query/client_management/invoice';
import {
  GET_CONDITIONS_META_FOR_OFFERED_ITEM,
  GET_CONDITIONS_META_FOR_WORKLOG_ITEM
} from '@nile/graphql/src/Query/client_management/client';
import { GroupBy } from '@nile/reports/src/modules/component';
import { Button } from '@superops/ui-design-system';
import { cloneDeep, orderBy } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import Select from 'react-select';
import { SVGIcon } from '@superops/super-icons';
import MetricConfig from '../MetricConfig';

const ChartConfig = (props) => {
  const { intl, errors, validateChartForm, meta, metaLoading } = props;

  const isMSPProduct = useProduct(COMMON_CONSTANTS.MSP);

  const context = useContext(ChartContext);

  const [modules, setModules] = useState([]);

  const { loading: operatorsLoading, data: allOperators } = useCachedQuery(
    GET_RULEBUILDER_OPERATORS
  );

  const {
    loading: ticketListViewMetaLoading,
    data: ticketListViewMeta
  } = useQuery(GET_TICKET_LISTVIEW_META, {
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
  });
  const { loading: assetViewMetaLoading, data: assetViewMeta } = useQuery(
    GET_ASSET_VIEW_META_INFO,
    {
      client: RMMClient,
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
    }
  );
  const { loading: patchMetaLoading, data: patchMeta } = useQuery(
    GET_PATCH_DETAILS_LISTVIEW_META,
    {
      client: RMMClient,
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
    }
  );
  const { loading: rmmPatchLoading, data: rmmPatchMeta } = useQuery(
    GET_CONDITIONS_META_FOR_RMM_REPORTS,
    {
      client: RMMClient,
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
    }
  );

  const { loading: rmmSoftwareMetaLoading, data: rmmSoftwareMeta } = useQuery(
    GET_SOFTWARELIST_META,
    {
      client: RMMClient,
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
    }
  );

  const { loading: rmmNMAssetMetaLoading, data: rmmNMAssetMeta } = useQuery(
    GET_NMAsset_META,
    {
      client: RMMClient,
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
    }
  );

  const { loading: rmmAllAssetsMetaLoading, data: rmmAllAssetsMeta } = useQuery(
    GET_ALL_ASSETS_META,
    {
      client: RMMClient,
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
    }
  );

  const { loading: alertMetaLoading, data: alertMeta } = useQuery(
    GET_ALERT_META_INFO_V4,
    {
      client: RMMClient,
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
    }
  );
  const { loading: invoiceMetaLoading, data: invoiceMeta } = useQuery(
    GET_CONDITIONS_META_FOR_INVOICE,
    {
      client: AccountClient,
      skip: !isMSPProduct,
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
    }
  );
  const { loading: offeredItemMetaLoading, data: offeredItemMeta } = useQuery(
    GET_CONDITIONS_META_FOR_OFFERED_ITEM,
    {
      client: AccountClient,
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
    }
  );

  const { loading: worklogMetaLoading, data: worklogMeta } = useQuery(
    GET_CONDITIONS_META_FOR_WORKLOG_ITEM,
    {
      client: AccountClient,
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
    }
  );

  const { loading: reportModulesLoading, data: reportModules } = useCachedQuery(
    GET_REPORT_MODULES,
    {
      client: AccountClient
    }
  );

  const formattedMeta = getGlobalEntityAttributes(intl, meta, [
    context?.value?.entity
  ]);

  useEffect(() => {
    if (reportModules?.getReportModules) {
      const allModules = reportModules?.getReportModules.map(
        ({ name, label }) => ({
          value: name,
          label: getTranslatedText(intl, label)
        })
      );
      setModules(allModules);
    }
  }, [reportModules]);

  const addMetricHandler = () => {
    const newMetricData = {
      name: `${getTranslatedText(
        intl,
        'reports.chart.metric.untitled'
      )} - ${context.value.metrics.length + 1}`,
      attribute: null,
      metricFunction: null,
      metricUnit: null,
      filterCondition: null
    };
    let updatedData = [newMetricData, ...cloneDeep(context.value.metrics)];
    updatedData = updatedData?.map((el, ind) => ({
      ...el,
      metricOrder: ind + 1
    }));
    context.updateContext({ ...context.value, metrics: updatedData });
  };

  if (
    operatorsLoading ||
    reportModulesLoading ||
    assetViewMetaLoading ||
    patchMetaLoading ||
    alertMetaLoading ||
    rmmPatchLoading ||
    invoiceMetaLoading ||
    ticketListViewMetaLoading ||
    rmmSoftwareMetaLoading ||
    worklogMetaLoading ||
    offeredItemMetaLoading ||
    rmmNMAssetMetaLoading ||
    rmmAllAssetsMetaLoading
  ) {
    return (
      <div className="flex actr jctr fw height400">
        <Loader />
      </div>
    );
  }

  const getModuleMeta = (module) => {
    const modulesMeta = {
      Ticket: ticketListViewMeta.getTicketListViewMetaInfo,
      WorkStation: assetViewMeta.getAssetViewConditionMetaInfo,
      Alert: alertMeta.getAlertViewMetaInfoV4,
      PatchDetail: patchMeta.getPatchDetailListViewMetaInfo,
      PatchHealth: rmmPatchMeta.getConditionsMetaForRmmReports,
      AntiVirusHealth: rmmPatchMeta.getConditionsMetaForRmmReports,
      DataBackupHealth: rmmPatchMeta.getConditionsMetaForRmmReports,
      Invoice: invoiceMeta?.getInvoiceListViewMeta,
      OfferedItem: offeredItemMeta.getOfferedItemListViewMeta,
      AssetSoftwareInventory: rmmSoftwareMeta.getConditionsMetaForRmmReports,
      HoursWorklogEntry: worklogMeta.getWorklogEntriesListViewMeta,
      NMAsset: rmmNMAssetMeta.getConditionsMetaForRmmReports,
      AllAssets: rmmAllAssetsMeta.getConditionsMetaForRmmReports
    };
    return modulesMeta[module] || null;
  };

  const metricAttributes = formattedMeta?.metricAttributes.map((attr) => ({
    ...attr,
    value: attr.name,
    label: attr.labelInMetric ? getTranslatedText(intl, attr.labelInMetric) : ''
  }));

  const checkForDefaultValue = (ent) => {
    if (ent === 'AssetSoftwareInventory') {
      return [{ attribute: 'assetSoftware.name' }];
    }
    return null;
  };

  const handleModuleChange = (mod) => {
    if (mod.value === context.value.entity) return;
    context.updateContext({
      ...context.value,
      entity: mod.value,
      metrics: [
        {
          name: `${getTranslatedText(
            intl,
            'reports.chart.metric.untitled'
          )} - 1`,
          attribute: null,
          metricFunction: null,
          metricUnit: null,
          filterCondition: null,
          metricOrder: 1
        }
      ],
      groupBy: checkForDefaultValue(mod.value),
      defaultChartType:
        mod.value === 'AssetSoftwareInventory' ? 'SUMMARY_TABLE' : null
    });
  };

  return (
    <div className="mbt10">
      <div className="flex fx-sb g10">
        <div className="w300" superid="moduleSelector">
          <Select
            value={modules.find((mod) => mod.value === context.value.entity)}
            label={getTranslatedText(intl, 'Module')}
            options={modules}
            onChange={handleModuleChange}
            components={{ IndicatorSeparator, DropdownArrow }}
            classNamePrefix="value_selector"
            formatGroupLabel={formatGroupLabel}
            menuPlacement="auto"
            placeholder={getTranslatedText(intl, 'Select module')}
            closeMenuOnScroll={(e) => {
              return (
                e.target &&
                e.target.classList &&
                !(e.target.classList.value.indexOf('menu-list') > -1)
              );
            }}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              menu: (base) => ({ ...base, zIndex: 9999 }),
              control: (base) => ({ ...base })
            }}
          />
        </div>
        {context.value.metrics?.length < MAX_METRIC_COUNT && (
          <Button
            id="addMetricBtn"
            onClick={addMetricHandler}
            disabled={context.value.metrics?.length === MAX_METRIC_COUNT}
            icon={<SVGIcon name="Add" />}
          >
            <FormattedMessage id="reports.chart.metric.btn" />
          </Button>
        )}
      </div>
      {!ticketListViewMetaLoading && !operatorsLoading && (
        <div className="mt-20 mb-20">
          {context.value.metrics?.map((metric, ind) => (
            <MetricConfig
              data={metric}
              id={ind}
              key={ind}
              index={ind}
              intl={props.intl}
              context={context}
              meta={getModuleMeta(context.value.entity)}
              validateChartForm={validateChartForm}
              metricAttributes={metricAttributes}
              allEntities={meta}
              allOperators={allOperators}
              errors={errors?.metrics?.[0]?.errors?.[ind]?.error}
              metaLoading={metaLoading}
            />
          ))}
        </div>
      )}
      <div className="card-border br10 p10">
        <GroupBy
          intl={intl}
          metaLoading={metaLoading}
          meta={meta}
          errors={errors?.groupBy}
          context={context}
        />
      </div>
    </div>
  );
};

export default injectIntl(ChartConfig);
